import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { driversApi } from '../api/drivers.api';
import { User } from '../types/user';
import { toast } from '../utils/toast';

interface EditDriverFormData {
  name: string;
  phone: string;
  status: 'active' | 'inactive';
}

export const DriversPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingDriver, setEditingDriver] = useState<User | null>(null);

  const [editFormData, setEditFormData] = useState<EditDriverFormData>({
    name: '',
    phone: '',
    status: 'active',
  });

  const [editFormErrors, setEditFormErrors] = useState<Partial<EditDriverFormData>>({});

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      setIsLoading(true);
      const data = await driversApi.listDrivers();
      setDrivers(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to load drivers:', error);
      toast.error(error.response?.data?.message || t('drivers.failedLoad'));
      setDrivers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditDriver = (driver: User) => {
    setEditingDriver(driver);
    setEditFormData({ name: driver.name, phone: driver.phone || '', status: driver.status });
    setEditFormErrors({});
    setIsEditModalOpen(true);
  };

  const handleEditInputChange = (field: keyof EditDriverFormData, value: string) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    if (editFormErrors[field]) {
      setEditFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateEditForm = (): boolean => {
    const errors: Partial<EditDriverFormData> = {};
    if (!editFormData.name.trim()) {
      errors.name = t('drivers.validation.nameRequired');
    }
    if (editFormData.phone && !/^\+?[0-9]{10,15}$/.test(editFormData.phone.replace(/\s/g, ''))) {
      errors.phone = t('drivers.validation.phoneInvalid');
    }
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEditForm()) return;
    if (!editingDriver) return;

    try {
      setIsSubmitting(true);
      await driversApi.updateDriver(editingDriver._id, {
        name: editFormData.name.trim(),
        phone: editFormData.phone.trim() || undefined,
        status: editFormData.status,
      });
      toast.success(t('drivers.editModal.updatedSuccess'));
      setIsEditModalOpen(false);
      setEditingDriver(null);
      await loadDrivers();
    } catch (error: any) {
      console.error('Failed to update driver:', error);
      toast.error(error.response?.data?.message || t('drivers.editModal.failedUpdate'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredDrivers = drivers.filter(
    (driver) =>
      driver.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      driver.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (driver.phone && driver.phone.includes(searchQuery))
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('drivers.title')}</h1>
            <p className="text-sm text-gray-600 mt-1">{t('drivers.subtitle')}</p>
          </div>
          <Button onClick={() => navigate('/users')} size="lg" variant="secondary">
            Gérer les utilisateurs
          </Button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <Input
            type="search"
            placeholder={t('drivers.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Drivers Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
              <p className="mt-4 text-gray-600">{t('drivers.loading')}</p>
            </div>
          ) : filteredDrivers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery ? t('drivers.noDriversFound') : t('drivers.noDriversYet')}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery ? t('drivers.trySearch') : t('drivers.getStarted')}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate('/users')}>
                  Créer un conducteur dans Utilisateurs
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('drivers.table.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('drivers.table.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('drivers.table.phone')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('drivers.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('drivers.table.created')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('drivers.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDrivers.map((driver) => (
                    <tr
                      key={driver._id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate('/drivers/' + driver._id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-700 font-medium text-sm">
                              {driver.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{driver.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{driver.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{driver.phone || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            driver.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {driver.status === 'active'
                            ? t('drivers.table.active')
                            : t('drivers.table.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(driver.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditDriver(driver); }}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                          title={t('drivers.table.editTitle')}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        {!isLoading && drivers.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('drivers.stats.total')}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{drivers.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('drivers.stats.active')}</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {drivers.filter((d) => d.status === 'active').length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('drivers.stats.inactive')}</div>
              <div className="text-2xl font-bold text-red-600 mt-1">
                {drivers.filter((d) => d.status === 'inactive').length}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Driver Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isSubmitting && setIsEditModalOpen(false)}
        title={t('drivers.editModal.title')}
        size="md"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label={t('drivers.editModal.fullName')}
            placeholder={t('drivers.editModal.namePlaceholder')}
            value={editFormData.name}
            onChange={(e) => handleEditInputChange('name', e.target.value)}
            error={editFormErrors.name}
            disabled={isSubmitting}
            autoFocus
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('drivers.editModal.email')}
            </label>
            <input
              type="email"
              value={editingDriver?.email || ''}
              disabled
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed"
            />
            <p className="mt-1 text-sm text-gray-500">{t('drivers.editModal.emailCannotChange')}</p>
          </div>
          <Input
            label={t('drivers.editModal.phone')}
            type="tel"
            placeholder={t('drivers.editModal.phonePlaceholder')}
            value={editFormData.phone}
            onChange={(e) => handleEditInputChange('phone', e.target.value)}
            error={editFormErrors.phone}
            disabled={isSubmitting}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('drivers.editModal.status')}
            </label>
            <select
              value={editFormData.status}
              onChange={(e) => handleEditInputChange('status', e.target.value)}
              disabled={isSubmitting}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="active">{t('drivers.editModal.statusActive')}</option>
              <option value="inactive">{t('drivers.editModal.statusInactive')}</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">{t('drivers.editModal.statusHelper')}</p>
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('drivers.editModal.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              {isSubmitting ? t('drivers.editModal.updatingDriver') : t('drivers.editModal.updateDriver')}
            </Button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};
